import React, { useEffect, useRef, useState } from 'react';
import { Button, ButtonToolbar, Row, Col } from 'reactstrap';
import { getInvoice, retryDepositHold, cancelDepositHold, approveDepositHold } from '@/service/program/invoice';
import { getDues } from './helper/getDues'
import { BillTo } from './components/BillTo'
import { DebitCredit } from './components/DebitCredit';
import JournalSummary from './components/JournalSummary';
import axios from 'axios'
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";

const ViewInvoice = (props) => {
    const [loading, setLoading] = useState(true)
    const [invoice, setInvoice] = useState(null)
    const [holdActionResult, setHoldActionResult] = useState(null)
    const [holdActionPending, setHoldActionPending] = useState(false)
    // A ref, not just state: a double click fires twice before the disabled state re-renders.
    const holdActionInFlight = useRef(false)
    const [reloadCount, setReloadCount] = useState(0)
    useEffect(() => {
        getInvoice(props.program.organization_id, props.program.id, props.invoice.id)
            .then(res => {
                setInvoice(res)
                setLoading(false)
            })
    }, [props, reloadCount])
    // A held or Failed hold is captured money not yet posted: staff can cancel either (the API
    // refunds the card where it holds a reference); a Failed one can also be retried once the
    // cause is fixed.
    const runHoldAction = async (action) => {
        if (holdActionInFlight.current) return
        holdActionInFlight.current = true
        setHoldActionPending(true)
        let result
        try {
            result = await action()
        } finally {
            holdActionInFlight.current = false
            setHoldActionPending(false)
        }
        const toText = (value) => (typeof value === 'string' && value) || null
        setHoldActionResult(result?.errors
            ? { ok: false, message: toText(result.errors) || 'The action could not be completed.' }
            : { ok: true, message: toText(result?.success) })
        setReloadCount((count) => count + 1)
    }
    const onRetryHold = (hold) =>
        runHoldAction(() => retryDepositHold(props.program.organization_id, props.program.id, props.invoice.id, hold.id))
    // The approve response is the updated hold row, not a { success } message. A hold past its
    // release time is released on approval, and that release can fail.
    const onApproveHold = (hold) =>
        runHoldAction(async () => {
            const result = await approveDepositHold(props.program.organization_id, hold.program_id ?? props.program.id, hold.id)
            if (result?.errors) return result
            if (result?.status === 'failed') {
                return { errors: `Approved, but the release failed: ${result.error || 'unknown error'}. Retry or cancel it.` }
            }
            return { success: result?.status === 'released' ? 'The held payment has been approved and released.' : 'The held payment has been approved.' }
        })
    const onCancelHold = (hold) => {
        const notes = (window.prompt('Reason for cancelling this held payment?') || '').trim()
        if (!notes) return
        runHoldAction(() => cancelDepositHold(props.program.organization_id, props.program.id, props.invoice.id, hold.id, notes))
    }
    const onClickDownloadInvoice = () => {
        axios.get(`/organization/${invoice.program.organization_id}/program/${invoice.program_id}/invoice/${invoice.id}/download`,
            {
                responseType: 'arraybuffer',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/pdf'
                }
            })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${invoice.invoice_number}.pdf`); //or any other extension
                document.body.appendChild(link);
                link.click();
            })
            .catch((error) => console.log(error));
    }
    const onClickPayInvoice = () => {
        props.setStep(3)
    }

    if (loading) return 'Loading...'
    if (!invoice) return 'Invoice cannot be loaded'

    const dues = getDues(invoice)
    const logoSrc = process.env.REACT_APP_API_STORAGE_URL + "/logo/big_logo.png";
    const formatAmount = (amt) => {
        if (amt == null) return amt;
        const s = String(amt);
        const hasDollar = s.indexOf('$') !== -1;
        const clean = s.replace(/[^0-9.\-]+/g, '');
        const num = parseFloat(clean);
        if (isNaN(num)) return amt;
        const match = clean.match(/\.(\d+)/);
        const decimals = match ? match[1].length : (s.indexOf('.') > -1 ? 0 : undefined);
        let formatted;
        if (decimals === undefined) formatted = num.toLocaleString();
        else formatted = num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        return hasDollar ? ('$' + formatted) : formatted;
    }
    // release_at/held_at are computed in US Eastern business hours on the backend; showing the
    // raw UTC string here can read a day off from what the hold actually means.
    const formatHoldTime = (isoString) => {
        if (!isoString) return isoString;
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return isoString;
        return date.toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' });
    }

    // With the hold off, release_at is the moment it was held: the payment releases on approval.
    const releaseTime = (hold) => {
        const awaitingApproval = hold.status === 'held' && hold.requires_approval && !hold.approved_at
        if (awaitingApproval && new Date(hold.release_at) <= new Date()) return 'On approval'
        return formatHoldTime(hold.release_at)
    }
    const approvalStatus = (hold) => {
        if (!hold.requires_approval) return '—'
        // A cancelled or failed hold is no longer waiting for anyone.
        if (!hold.approved_at) return hold.status === 'held' ? 'Awaiting Super Admin approval' : 'Not approved'
        return `Approved${hold.approved_by ? ` by ${hold.approved_by}` : ''} on ${formatHoldTime(hold.approved_at)}`
    }

    return (
        <>
            <Row className='mb-4'>
                <Col md="3" lg="3" xl="3">
                    <div className="modal__title">
                        <h3 className="mb-4">View Invoice </h3>
                    </div>
                </Col>
                <Col md="9" lg="9" xl="9" className="text-right">
                    <ButtonToolbar className="modal__footer flex justify-content-right w100">
                        <Button
                            color="primary"
                            className="mr-3"
                            onClick={() => onClickDownloadInvoice()}
                        >
                            Download Invoice
                        </Button>
                        {

                            props.auth?.isSuperAdmin && !props.hidePayInvoice &&
                            <Button
                                color="primary"
                                className="mr-3"
                                onClick={() => onClickPayInvoice()}
                            >
                                Pay Invoice
                            </Button>
                        }
                        <Button
                            outline
                            color="primary"
                            className="mr-0"
                            onClick={() => props.setStep(0)}
                        >
                            Back
                        </Button>
                    </ButtonToolbar>
                </Col>
            </Row>
            <Row>
                <Col md="6" lg="6" xl="6">
                    <div className="mb-4">
                        <img src={logoSrc} style={{ 'maxWidth': '200px' }} />
                    </div>
                    <div className="mb-2">
                        <p>
                            3801 PGA Blvd <br /> Suite 600 <br /> Palm Beach Gardens, FL 33410
                        </p>
                    </div>
                    <div>
                        <p>
                            Bill To : <br />
                            <BillTo invoice={invoice} key={'billto-item'} />
                        </p>
                    </div>
                </Col>
                <Col md="6" lg="6" xl="6">
                    <div className="text-right float-right">
                        <span className='outline'>Invoice # {invoice.invoice_number}</span>
                        <div className="d-flex justify-content-between">
                            <div>Date: </div>
                            <div>{invoice.date_end}</div>
                        </div>
                        <div className="d-flex justify-content-between">
                            <div className={invoice.date_due === invoice.date_end ? "d-none" :""}>Due Date: </div>
                            <div>{invoice.date_due === invoice.date_end ? 'Due upon receipt' : invoice.date_due}</div>
                        </div>
                        {invoice.invoice_po_number &&
                            <div className="d-flex justify-content-between">
                                <div>PO Number: </div>
                                <div>{invoice.invoice_po_number}</div>
                            </div>}
                        <div className="d-flex justify-content-between">
                            <div>{dues.label}: </div>
                                <div>{formatAmount(dues.amount)}</div>
                        </div>
                        {invoice.held_amount > 0 &&
                            <div className="d-flex justify-content-between">
                                <div>Held Amount: </div>
                                <div>{formatAmount(`$${Number(invoice.held_amount).toFixed(2)}`)}</div>
                            </div>}
                    </div>
                    <div style={{ clear: 'both', 'fontSize': '11px' }}>
                        <br />
                        <table className='float-right'>
                            <tbody>
                                <tr>
                                    <td></td>
                                    <td><u>Wire Transfer</u></td>
                                    <td><u>ACH Payment</u></td>
                                </tr>
                                <tr>
                                    <td>Routing Number (RTN/ABA):</td>
                                    <td>021000021</td>
                                    <td>102001017</td>
                                </tr>
                                <tr>
                                    <td>Account Number:</td>
                                    <td>138091170</td>
                                    <td>138091170</td>
                                </tr>
                                <tr>
                                    <td>Bank:</td>
                                    <td>Chase Bank, NA</td>
                                    <td>Chase Bank, NA</td>
                                </tr>
                                <tr>
                                    <td valign="top">Address:</td>
                                    <td>2696 S Colorado Blvd&nbsp;&nbsp;&nbsp;<br /> Denver, CO 80222
                                    </td>
                                    <td>2696 S Colorado Blvd<br /> Denver, CO 80222
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Col>
            </Row>
            <JournalSummary invoice={invoice} />
            <br />
            <h4 className="text-center" style={{ border: '1px solid #ccc' }}>Invoice Details</h4>
            <br />
            <Row>
                <Col className='table-responsive'>
                    <table className='table table-hover'>
                        <tbody>
                            <tr>
                                <th>Program</th>
                                <th>Description</th>
                                <th style={{ textAlign: 'right' }}>Qty</th>
                                <th style={{ textAlign: 'right' }}>Price</th>
                                <th style={{ textAlign: 'right' }}>Total</th>
                            </tr>
                            <DebitCredit invoice={invoice} />
                        </tbody>
                    </table>
                </Col>
            </Row>
            {invoice.deposit_holds && invoice.deposit_holds.length > 0 &&
                <>
                    <br />
                    <h4 className="text-center" style={{ border: '1px solid #ccc' }}>Deposit Holds</h4>
                    <br />
                    {holdActionResult?.message &&
                        <div className={`alert ${holdActionResult.ok ? 'alert-success' : 'alert-danger'}`}>{holdActionResult.message}</div>}
                    <Row>
                        <Col className='table-responsive'>
                            <table className='table table-hover'>
                                <tbody>
                                    <tr>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Held At</th>
                                        <th>Release At</th>
                                        <th>Approval</th>
                                        <th>Gateway</th>
                                        <th></th>
                                    </tr>
                                    {invoice.deposit_holds.map((hold) => (
                                        <tr key={hold.id}>
                                            <td>{formatAmount(`$${Number(hold.amount).toFixed(2)}`)}</td>
                                            <td>
                                                {hold.status}
                                                {hold.status === 'failed' && hold.error &&
                                                    <div className="text-danger small">{hold.error}</div>}
                                            </td>
                                            <td>{formatHoldTime(hold.held_at)}</td>
                                            <td>{releaseTime(hold)}</td>
                                            <td>{approvalStatus(hold)}</td>
                                            {/* What staff search the gateway's portal by when a cancel needs a manual refund. */}
                                            <td>{hold.payment_gateway
                                                ? `${hold.payment_gateway}${hold.gateway_transaction_id ? ` #${hold.gateway_transaction_id}` : ''}`
                                                : ''}</td>
                                            <td>
                                                {props.auth?.isSuperAdmin && (hold.status === 'held' || hold.status === 'failed') &&
                                                    <ButtonToolbar>
                                                        {hold.status === 'held' && hold.requires_approval && !hold.approved_at &&
                                                            <Button size="sm" color="success" disabled={holdActionPending} onClick={() => onApproveHold(hold)}>Approve</Button>}
                                                        {hold.status === 'failed' &&
                                                            <Button size="sm" color="primary" disabled={holdActionPending} onClick={() => onRetryHold(hold)}>Retry release</Button>}
                                                        <Button size="sm" color="danger" outline disabled={holdActionPending} onClick={() => onCancelHold(hold)}>Cancel</Button>
                                                    </ButtonToolbar>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Col>
                    </Row>
                </>}
        </>
    )
}

export default withRouter(connect((props) => ({
    auth: props.auth
}))(ViewInvoice));
//export default ViewInvoice;